import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Instagram, Facebook } from 'lucide-react';
import { navigation, siteConfig } from '../mock';

// Small helper — desktop social row (icons on lg, icons + profile names on xl+)
const DesktopSocials = () => (
  <div className="hidden lg:flex items-center gap-4 xl:gap-5 pl-4 border-l border-[var(--line)]">
    {siteConfig.socials.map((s) => {
      const Icon = s.key === 'instagram' ? Instagram : Facebook;
      return (
        <a
          key={s.key}
          href={s.url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={s.label}
          title={s.label}
          className="group flex items-center gap-2 text-brand hover:text-gold transition-colors"
        >
          <Icon className="w-[18px] h-[18px]" strokeWidth={1.4} />
          <span className="hidden xl:inline text-[11px] tracking-[0.14em] uppercase">
            {s.label}
          </span>
        </a>
      );
    })}
  </div>
);

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-30 transition-colors duration-300 ${
          scrolled ? 'bg-white/85 backdrop-blur-md border-b border-[var(--line)]' : ''
        }`}
      >
        <div className="flex items-center justify-between px-6 md:px-10 py-5 gap-6">
          <Link to="/" className="flex items-center gap-3 flex-shrink-0">
            <span className="font-serif-display text-brand text-xl md:text-2xl tracking-[0.28em]">
              PRESTIGE
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-7 xl:gap-9">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={`text-[11px] tracking-[0.22em] uppercase transition-colors ${
                    isActive ? 'text-gold' : 'text-brand hover:text-gold'
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-4">
            <DesktopSocials />
            <a
              href={siteConfig.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden lg:inline-flex items-center justify-center px-6 py-2.5 rounded-full border border-[var(--gold)] text-[11px] tracking-[0.22em] uppercase text-gold hover:bg-[var(--gold)] hover:text-white transition-colors duration-300"
            >
              Broneerimine
            </a>

            <button
              onClick={() => setOpen(true)}
              aria-label="Ava menüü"
              className="lg:hidden w-10 h-10 flex items-center justify-center rounded-full bg-white border border-[var(--line)] hover:border-[var(--gold)] transition-colors"
            >
              <Menu className="w-4 h-4 text-brand" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </header>

      {open && (
        <>
          <div className="menu-overlay" onClick={() => setOpen(false)} />
          <div className="menu-panel flex flex-col">
            <div className="flex justify-between items-center mb-14 flex-shrink-0">
              <span className="font-serif-display text-brand text-lg tracking-[0.28em]">
                PRESTIGE
              </span>
              <button
                onClick={() => setOpen(false)}
                aria-label="Sulge menüü"
                className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-[var(--ivory-2)] transition-colors"
              >
                <X className="w-4 h-4 text-brand" strokeWidth={1.5} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col gap-5 overflow-y-auto">
              {navigation.map((item) => {
                const isActive = location.pathname === item.href;
                const cls = `font-serif-display text-2xl md:text-3xl tracking-wide transition-colors ${
                  isActive ? 'text-gold' : 'text-brand hover:text-gold'
                }`;
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={() => setOpen(false)}
                    className={cls}
                  >
                    {item.label}
                  </Link>
                );
              })}
              <a
                href={siteConfig.bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                className="mt-4 inline-flex items-center justify-center px-6 py-3 rounded-full border border-[var(--gold)] text-xs tracking-[0.22em] uppercase text-gold hover:bg-[var(--gold)] hover:text-white transition-colors duration-300 self-start"
              >
                Broneerimine
              </a>

              <div className="mt-8 pt-6 border-t border-[var(--line)] flex flex-col gap-4">
                {siteConfig.socials.map((s) => {
                  const Icon = s.key === 'instagram' ? Instagram : Facebook;
                  return (
                    <a
                      key={s.key}
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setOpen(false)}
                      className="flex items-center gap-3 text-brand hover:text-gold transition-colors"
                    >
                      <Icon className="w-[18px] h-[18px]" strokeWidth={1.4} />
                      <span className="text-sm tracking-wide">{s.label}</span>
                    </a>
                  );
                })}
              </div>
            </nav>
            <div className="text-sm text-brand-muted space-y-2 pt-8 mt-8 border-t border-[var(--line)] flex-shrink-0">
              <p>{siteConfig.contact.phone}</p>
              <p>{siteConfig.contact.email}</p>
              <p>{siteConfig.contact.address}</p>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Header;
